import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PageHeader from "../components/PageHeader";
import { getApiBase } from "../api";

const MAPBOX_TOKEN = "pk.eyJ1Ijoicm9hZG9wdGl4IiwiYSI6ImNtazV0d2MxcTBuN3Eza29sbXJpaGJ3Z2EifQ.hX96aHeeThw3QT7aPGDWZQ";

export default function BrokerTracking() {
  const apiBase = useMemo(() => getApiBase(), []);
  const [activeLoads, setActiveLoads] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const loadActiveLoads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/tracking/active`, {
        headers: { "x-admin-email": "admin@roadoptix.com" }
      });
      if (res.ok) {
        const data = await res.json();
        setActiveLoads(data.loads || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadActiveLoads();
    const interval = setInterval(loadActiveLoads, 30000);
    return () => clearInterval(interval);
  }, [loadActiveLoads]);

  // Initialize Mapbox once on mount
  useEffect(() => {
    let mounted = true;
    
    const initMap = async () => {
      // Wait for container to be available
      if (!mapContainerRef.current) {
        setTimeout(initMap, 100);
        return;
      }
      
      if (mapRef.current) return;

      try {
        // Load Mapbox GL JS dynamically
        if (!window.mapboxgl) {
          const link = document.createElement('link');
          link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
          link.rel = 'stylesheet';
          document.head.appendChild(link);

          const script = document.createElement('script');
          script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
          document.body.appendChild(script);

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
          });
        }

        if (!mounted) return;

        window.mapboxgl.accessToken = MAPBOX_TOKEN;
        
        mapRef.current = new window.mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [-95.3698, 29.7604], // Houston as default
          zoom: 5
        });

        mapRef.current.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
        
        mapRef.current.on('load', () => {
          if (mounted) setMapLoaded(true);
        });

      } catch (err) {
        console.error('Mapbox init error:', err);
      }
    };

    initMap();

    return () => {
      mounted = false;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Update map markers when tracking data changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !trackingData) return;

    // Clear existing markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // Remove existing route layer
    try {
      if (mapRef.current.getLayer('route')) mapRef.current.removeLayer('route');
      if (mapRef.current.getSource('route')) mapRef.current.removeSource('route');
    } catch (e) {}

    const { load, tracking } = trackingData;
    const bounds = new window.mapboxgl.LngLatBounds();
    let hasPoints = false;

    // Origin marker
    if (load.originLat && load.originLng) {
      const el = document.createElement('div');
      el.innerHTML = '📦';
      el.style.fontSize = '28px';
      el.style.cursor = 'pointer';
      const marker = new window.mapboxgl.Marker(el)
        .setLngLat([load.originLng, load.originLat])
        .setPopup(new window.mapboxgl.Popup().setHTML(`<b>Pickup</b><br/>${load.originAddress || ''}`))
        .addTo(mapRef.current);
      markersRef.current.push(marker);
      bounds.extend([load.originLng, load.originLat]);
      hasPoints = true;
    }

    // Destination marker
    if (load.destLat && load.destLng) {
      const el = document.createElement('div');
      el.innerHTML = '🏁';
      el.style.fontSize = '28px';
      el.style.cursor = 'pointer';
      const marker = new window.mapboxgl.Marker(el)
        .setLngLat([load.destLng, load.destLat])
        .setPopup(new window.mapboxgl.Popup().setHTML(`<b>Delivery</b><br/>${load.destAddress || ''}`))
        .addTo(mapRef.current);
      markersRef.current.push(marker);
      bounds.extend([load.destLng, load.destLat]);
      hasPoints = true;
    }

    // Current truck location
    if (tracking.lastUpdate?.latitude && tracking.lastUpdate?.longitude) {
      const el = document.createElement('div');
      el.innerHTML = '🚛';
      el.style.fontSize = '32px';
      el.style.cursor = 'pointer';
      const marker = new window.mapboxgl.Marker(el)
        .setLngLat([tracking.lastUpdate.longitude, tracking.lastUpdate.latitude])
        .setPopup(new window.mapboxgl.Popup().setHTML(
          `<b>🚛 Current Location</b><br/>` +
          `${tracking.lastUpdate.city || ''}, ${tracking.lastUpdate.state || ''}<br/>` +
          `<small>${new Date(tracking.lastUpdate.recordedAt).toLocaleString()}</small>`
        ))
        .addTo(mapRef.current);
      markersRef.current.push(marker);
      bounds.extend([tracking.lastUpdate.longitude, tracking.lastUpdate.latitude]);
      hasPoints = true;
    }

    // Draw route line from history
    if (tracking.history?.length > 1) {
      const coordinates = tracking.history.map(loc => [loc.longitude, loc.latitude]);
      
      mapRef.current.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates }
        }
      });

      mapRef.current.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#3b82f6', 'line-width': 4, 'line-opacity': 0.8 }
      });

      coordinates.forEach(coord => bounds.extend(coord));
    }

    if (hasPoints) {
      mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 14 });
    }
  }, [trackingData, mapLoaded]);

  async function loadTrackingDetails(loadId) {
    setSelectedLoad(loadId);
    try {
      const res = await fetch(`${apiBase}/tracking/loads/${loadId}`, {
        headers: { "x-admin-email": "admin@roadoptix.com" }
      });
      if (res.ok) {
        const data = await res.json();
        setTrackingData(data);
      }
    } catch (e) {
      setError(e.message);
    }
  }

  function formatTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString();
  }

  function formatTimeAgo(iso) {
    if (!iso) return "";
    const mins = Math.floor((new Date() - new Date(iso)) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  function getStatusColor(status) {
    const colors = { ACCEPTED: "#3b82f6", PICKED_UP: "#8b5cf6", DELIVERED: "#10b981" };
    return colors[status] || "#6b7280";
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader title="📍 GPS Tracking" subtitle="Real-time location tracking for active loads" />

      {error && <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, marginBottom: 16, color: "#991b1b" }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 20 }}>
        {/* Load List */}
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <div style={{ padding: 14, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontWeight: 700 }}>Active Loads ({activeLoads.length})</h3>
            <button onClick={loadActiveLoads} disabled={loading} style={{ padding: "5px 10px", fontSize: 12, cursor: "pointer" }}>
              🔄 {loading ? "..." : "Refresh"}
            </button>
          </div>
          
          <div style={{ maxHeight: 550, overflowY: "auto" }}>
            {activeLoads.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
                <div style={{ fontSize: 40 }}>🚛</div>
                <div>No active loads</div>
              </div>
            ) : activeLoads.map(load => (
              <div 
                key={load.id}
                onClick={() => loadTrackingDetails(load.id)}
                style={{
                  padding: 12,
                  borderBottom: "1px solid #f3f4f6",
                  cursor: "pointer",
                  background: selectedLoad === load.id ? "#eff6ff" : "white"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: getStatusColor(load.status), color: "white" }}>
                    {load.status}
                  </span>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{load.referenceNumber || load.id.slice(0, 8)}</span>
                  {load.lastLocationTime && <span style={{ marginLeft: "auto", color: "#ef4444", fontSize: 10 }}>📍</span>}
                </div>
                <div style={{ fontSize: 12, color: "#4b5563" }}>
                  {load.originCity || "?"}, {load.originState || "?"} → {load.destCity || "?"}, {load.destState || "?"}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  🚛 {load.carrierName || "Unknown"}
                  {load.lastLocationTime && <span style={{ marginLeft: 8, color: "#ef4444" }}>📍 {formatTimeAgo(load.lastLocationTime)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map & Details */}
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          {/* Header */}
          {trackingData ? (
            <div style={{ padding: 14, borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ padding: "3px 8px", borderRadius: 5, fontSize: 11, fontWeight: 700, background: getStatusColor(trackingData.load.status), color: "white" }}>
                  {trackingData.load.status}
                </span>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{trackingData.load.referenceNumber || trackingData.load.id.slice(0, 8)}</span>
                <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>🚛 {trackingData.load.carrier?.name}</span>
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: "#4b5563" }}>
                📦 {trackingData.load.originAddress?.slice(0, 30)} → 🏁 {trackingData.load.destAddress?.slice(0, 30)}
              </div>
            </div>
          ) : (
            <div style={{ padding: 14, borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
              <span style={{ color: "#9ca3af" }}>Select a load to view tracking</span>
            </div>
          )}

          {/* Map */}
          <div 
            ref={mapContainerRef}
            style={{ height: 350, width: "100%", background: "#e5e7eb" }}
          />

          {/* Current Location */}
          {trackingData?.tracking?.lastUpdate ? (
            <div style={{ padding: 14, borderTop: "1px solid #e5e7eb", background: "#ecfdf5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 28 }}>🚛</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: "#065f46" }}>
                    {trackingData.tracking.lastUpdate.city || "Unknown"}, {trackingData.tracking.lastUpdate.state || ""}
                  </div>
                  <div style={{ fontSize: 12, color: "#047857" }}>
                    {trackingData.tracking.lastUpdate.latitude?.toFixed(4)}, {trackingData.tracking.lastUpdate.longitude?.toFixed(4)} • {formatTime(trackingData.tracking.lastUpdate.recordedAt)}
                  </div>
                </div>
                <a 
                  href={`https://www.google.com/maps?q=${trackingData.tracking.lastUpdate.latitude},${trackingData.tracking.lastUpdate.longitude}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ padding: "6px 12px", background: "#2563eb", color: "white", borderRadius: 6, textDecoration: "none", fontSize: 12, fontWeight: 600 }}
                >
                  🗺️ Maps
                </a>
              </div>
            </div>
          ) : selectedLoad && (
            <div style={{ padding: 20, textAlign: "center", color: "#9ca3af", borderTop: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 24 }}>🚛</div>
              <div>No location updates yet</div>
            </div>
          )}

          {/* History */}
          {trackingData?.tracking?.history?.length > 0 && (
            <div style={{ padding: 14, borderTop: "1px solid #e5e7eb", maxHeight: 180, overflowY: "auto" }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>📋 History ({trackingData.tracking.history.length})</div>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <tbody>
                  {trackingData.tracking.history.slice().reverse().slice(0, 8).map((loc, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: 4 }}>{formatTime(loc.recordedAt)}</td>
                      <td style={{ padding: 4 }}>{loc.city || `${loc.latitude?.toFixed(3)}, ${loc.longitude?.toFixed(3)}`}</td>
                      <td style={{ padding: 4 }}><span style={{ padding: "1px 4px", borderRadius: 3, fontSize: 9, background: "#f3f4f6" }}>{loc.source}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
